import { useQuery } from "@apollo/client";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { Box, CircularProgress, Divider, Link as MuiLink, Stack, Typography } from "@mui/material";
import { StatusChip } from "components/StatusChip";
import CopyableTypography from "components/Typography/CopyableTypography";
import { gql } from "generated-graphql";
import { InboundEmailDetailQuery } from "generated-graphql/graphql";
import { prettyPrintDateTimeMed } from "util/dates";

import {
  attachmentsLabel,
  ATTACHMENT_CODE_LABELS,
  missingAttachmentDetailText,
  missingReplyText,
  outcomeDisplay,
  REJECTION_HELP,
} from "./displayText";
import { DETAIL_PANEL_INDENT_PX } from "./inboundGridColumns";

const DETAIL = gql(`
  query InboundEmailDetail($id: ID!) {
    receivedEmail(id: $id) {
      id
      receivedAt
      fromAddress
      envelopeSender
      recipients
      subject
      outcome
      rejectionReason
      attachmentsProcessedCount
      attachmentsTotalCount
      bodyText
      hasHtmlBody
      senderIp
      verdictSpam
      verdictVirus
      verdictSpf
      verdictDkim
      verdictDmarc
      sesMessageId
      matchedSender {
        id
        value
        label
      }
      tenant {
        id
        name
      }
      attachments {
        id
        filename
        accepted
        messageCode
        document {
          id
          title
          extractionStatus
        }
      }
      reply {
        id
        to
        sentAt
      }
      documents {
        id
        title
        documentType
        createdAt
        extractionStatus
      }
    }
  }
`);

/**
 * Where a document this email produced lives in the customer's app.
 *
 * Same shape the staff Manifest Documents grid uses, including seeding the
 * destination's omnisearch so it filters to this document rather than merely
 * selecting it in a list of everything.
 */
function customerDocumentHref(tenantId: string, documentId: string): string {
  return `/o/${tenantId}/documents/${documentId}?omnisearch=${encodeURIComponent(
    `id:${documentId}`
  )}`;
}

/**
 * A document, linked into the customer's app when we know whose it is.
 *
 * Falls back to plain text rather than a dead link: an email that resolved to no
 * organization has no customer app to point at.
 */
function DocumentLink({
  tenantId,
  documentId,
  title,
}: {
  tenantId: string | undefined;
  documentId: string;
  title: string;
}) {
  if (!tenantId) {
    return <>{title}</>;
  }
  return (
    <MuiLink
      href={customerDocumentHref(tenantId, documentId)}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
    >
      {title}
      <OpenInNew fontSize="inherit" />
    </MuiLink>
  );
}

/** A PASS is the boring case; only a failure earns colour. */
function Verdict({ label, value }: { label: string; value: string }) {
  const passed = value.toUpperCase() === "PASS";
  return (
    <StatusChip
      color={passed ? "default" : "error"}
      label={`${label} ${value.toUpperCase()}`}
      sx={{ maxWidth: "100%" }}
    />
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {/* A div, not the default p: the value slot takes any node, and several fields
          pass an element that renders a block of its own. Nesting one inside a p is
          invalid HTML and React reports it as a hydration error. */}
      <Typography component="div" variant="body2" sx={{ wordBreak: "break-word" }}>
        {children}
      </Typography>
    </Stack>
  );
}

type InboundEmail = NonNullable<InboundEmailDetailQuery["receivedEmail"]>;

/**
 * Per-attachment outcomes: what filed, what was refused, and why.
 *
 * The reason this panel exists. These outcomes used to be computed, sent to the
 * sender, and discarded — so the sender was told "manifest2.pdf is not a PDF" while
 * staff could see only "2 of 5".
 */
function AttachmentList({ email }: { email: InboundEmail }) {
  return (
    <>
      {email.attachments.length === 0 &&
        missingAttachmentDetailText(email.attachmentsTotalCount) && (
          <Typography variant="body2" color="text.secondary">
            {missingAttachmentDetailText(email.attachmentsTotalCount)}
          </Typography>
        )}

      {email.attachments.length > 0 && (
        <Stack spacing={0.5}>
          <Typography variant="overline" color="text.secondary">
            Attachments
          </Typography>
          {email.attachments.map((attachment) => (
            <Stack
              key={attachment.id}
              direction="row"
              spacing={1}
              alignItems="baseline"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography
                variant="body2"
                color={attachment.accepted ? "success.main" : "error.main"}
              >
                {attachment.accepted ? "✓" : "✕"}
              </Typography>
              <Typography variant="body2">
                {attachment.document ? (
                  <DocumentLink
                    tenantId={email.tenant?.id}
                    documentId={attachment.document.id}
                    title={attachment.filename ?? "(no filename)"}
                  />
                ) : (
                  (attachment.filename ?? "(no filename)")
                )}
              </Typography>
              {attachment.messageCode && (
                <Typography variant="body2" color="text.secondary">
                  {ATTACHMENT_CODE_LABELS[attachment.messageCode]}
                </Typography>
              )}
              {attachment.document?.extractionStatus && (
                <Typography variant="caption" color="text.secondary">
                  · parse: {attachment.document.extractionStatus}
                </Typography>
              )}
            </Stack>
          ))}
        </Stack>
      )}
    </>
  );
}

/** The body of the message that arrived, when it came as text. */
function MessageBody({
  bodyText,
  hasHtmlBody,
}: {
  bodyText?: string | null;
  hasHtmlBody: boolean;
}) {
  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary">
        Message body
      </Typography>
      {bodyText ? (
        <Typography
          variant="body2"
          // Plain text in a pre-wrap block: the API deliberately never returns the
          // HTML body, so there is nothing here to render as markup.
          sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {bodyText}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {hasHtmlBody
            ? "This message had an HTML body only, which is not shown."
            : "No message body."}
        </Typography>
      )}
    </Stack>
  );
}

/**
 * What we kept about one inbound email, for a staff member working out what happened
 * to it — until this existed, the only record of why a message was refused was in
 * CloudWatch and in the reply the sender got.
 *
 * The body renders as plain text because that is all the API returns. The message is
 * written by whoever sent it and the intake address accepts mail from anyone, so
 * rendering its HTML here would make every inbound message a stored-XSS attempt.
 */
export function InboundEmailDetail({ id }: { id: string }) {
  const { data, loading, error } = useQuery(DETAIL, { variables: { id } });
  const email = data?.receivedEmail;

  if (loading && !email) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (error || !email) {
    return (
      <Box sx={{ py: 2, pr: 2, pl: `${DETAIL_PANEL_INDENT_PX}px` }}>
        <Typography variant="body2" color="text.secondary">
          {error ? `Couldn't load this email. ${error.message}` : "This email is no longer stored."}
        </Typography>
      </Box>
    );
  }

  const outcome = outcomeDisplay(email);
  const reasonHelp = email.rejectionReason ? REJECTION_HELP[email.rejectionReason] : null;

  return (
    // Indented past the chevron column so the panel's content starts where the row's
    // own data does. Flush against the grid's left edge, it read as a separate block
    // that happened to appear rather than as this row opening up.
    <Box
      sx={(theme) => ({
        py: 2,
        pr: 2,
        pl: `${DETAIL_PANEL_INDENT_PX}px`,
        bgcolor: "action.hover",
        height: "100%",
        overflow: "auto",
        // A drawn scrollbar rather than the platform's overlay one, which on macOS
        // fades out at rest and left no sign that this panel scrolls at all.
        //
        // Two things have to be true together. Declaring a `::-webkit-scrollbar` width
        // is what makes Chrome and Safari reserve a permanent gutter instead of
        // overlaying — but it also opts out of drawing the native thumb, so a colour
        // here is not decoration, it is the only thing rendered. Omit it and the result
        // is an 8px empty gutter, which looks exactly like no scrollbar at all.
        //
        // Real colour strings off the theme, not `sx` palette tokens: `background`
        // is not palette-aware (only `backgroundColor`/`bgcolor` are) and a token
        // there is dropped silently, which is a quiet way to think you have styled
        // something you have not.
        "&::-webkit-scrollbar": { width: 8 },
        "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.action.active,
          borderRadius: "4px",
          "&:hover": { backgroundColor: theme.palette.text.primary },
        },
        // Firefox has no thumb pseudo-element. Scoped, because setting
        // `scrollbar-width` unconditionally makes Chrome ignore the rules above and
        // go back to the overlay bar.
        "@supports not selector(::-webkit-scrollbar)": {
          scrollbarWidth: "thin",
          scrollbarColor: `${theme.palette.action.active} transparent`,
        },
      })}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <StatusChip color={outcome.color} label={outcome.label} />
          <Typography variant="subtitle2">{email.subject || "(no subject)"}</Typography>
        </Stack>

        {reasonHelp && (
          <Typography variant="body2" color="text.secondary">
            {reasonHelp}
          </Typography>
        )}

        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
          }}
        >
          <Field label="From (header)">{email.fromAddress ?? "—"}</Field>
          {/* Shown because they legitimately differ, and resolution matches on the
              header — conflating them is what sends someone to allowlist a bounce
              address that can never match. */}
          <Field label="Envelope sender">{email.envelopeSender}</Field>
          <Field label="Sent to">{email.recipients.join(", ") || "—"}</Field>
          <Field label="Received">{prettyPrintDateTimeMed(email.receivedAt)}</Field>
          <Field label="Attachments">{attachmentsLabel(email)}</Field>
          <Field label="Matched allowlist entry">
            {email.matchedSender
              ? (email.matchedSender.label ?? email.matchedSender.value)
              : "None — nothing accepted it"}
          </Field>
          <Field label="Organization">
            {email.tenant?.name ?? "Unattributed — no organization matched"}
          </Field>
          {/* Sent, and to whom. Never "delivered": nothing records a bounce, so
              claiming arrival would assert something we do not know. */}
          <Field label="Reply sent">
            {email.reply
              ? `${email.reply.to} · ${prettyPrintDateTimeMed(email.reply.sentAt)}`
              : missingReplyText(email.outcome, Boolean(email.tenant))}
          </Field>
          <Field label="Sender IP">{email.senderIp}</Field>
          {/* Click to copy, rather than a link. The raw message is in S3 and the
              lambda's logs are in CloudWatch — both bucket and log group are
              environment-specific and reachable only by an engineer, so building
              console URLs into the page would age badly and mostly 403. Handing over
              the id is the useful part. */}
          <Field label="SES message id">
            <CopyableTypography
              text={email.sesMessageId}
              variant="body2"
              sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
            />
          </Field>
        </Box>

        <Divider />

        <AttachmentList email={email} />

        <Divider />

        <Stack spacing={1}>
          <Typography variant="caption" color="text.secondary">
            SES checks
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Verdict label="Spam" value={email.verdictSpam} />
            <Verdict label="Virus" value={email.verdictVirus} />
            <Verdict label="SPF" value={email.verdictSpf} />
            <Verdict label="DKIM" value={email.verdictDkim} />
            <Verdict label="DMARC" value={email.verdictDmarc} />
          </Stack>
        </Stack>

        <Divider />

        <Stack spacing={1}>
          <Typography variant="caption" color="text.secondary">
            Documents created ({email.documents.length})
          </Typography>
          {email.documents.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              None.{" "}
              {(email.attachmentsTotalCount ?? 0) > 0
                ? "The attachments were refused, or processing did not finish."
                : "The message carried no attachments."}
            </Typography>
          ) : (
            email.documents.map((doc) => (
              <Typography key={doc.id} variant="body2">
                <DocumentLink
                  tenantId={email.tenant?.id}
                  documentId={doc.id}
                  title={doc.title ?? "(untitled)"}
                />
                <Typography component="span" variant="caption" color="text.secondary">
                  {" · "}
                  {doc.documentType}
                </Typography>
              </Typography>
            ))
          )}
        </Stack>

        <Divider />

        <MessageBody bodyText={email.bodyText} hasHtmlBody={email.hasHtmlBody} />
      </Stack>
    </Box>
  );
}
